import {
  Box,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
  SxProps,
} from "@mui/material";

import { Printer } from "lucide-react";
import { useTranslation } from "react-i18next";

import { useListSubjectsSubjectsGetQuery } from "api";
import AppVersion from "components/AppVersion/AppVersion";
import { DynamicButton } from "components/DynamicButton/DynamicButton";
import { useGetZones } from "hooks/useGetZones";

import { NightReportLanding } from "./components/NightReportLanding";

import { ZoneSelector } from "../components/ZoneSelector";
import { useMonitoringStore } from "../monitoringStore";

type NightReportFilterProps = {
  sx?: SxProps;
};

function NightReportFilter({ sx = {} }: NightReportFilterProps) {
  const { t } = useTranslation();

  const nightReportFilter = useMonitoringStore(
    (state) => state.nightReportFilter,
  );
  const setNightReportFilter = useMonitoringStore(
    (state) => state.setNightReportFilter,
  );

  return (
    <FormControlLabel
      slotProps={{ typography: { variant: "bodySmallRegular" } }}
      control={
        <Switch
          name="nightReportFilter"
          id="nightReportFilter"
          checked={nightReportFilter?.showOnlyBadNights}
          onChange={(e) =>
            setNightReportFilter({
              ...nightReportFilter,
              showOnlyBadNights: e.target.checked,
            })
          }
          sx={{ marginRight: 1 }}
        />
      }
      label={t("only_view_bad_night_sleep")}
      sx={{ marginTop: 0.5, marginLeft: 1.5, ...sx }}
    />
  );
}

export function NightReportScreen() {
  const { t } = useTranslation();
  const { zones, isLoading: isLoadingZones } = useGetZones();

  const selectedZoneIds = useMonitoringStore((state) => state.selectedZoneIds);

  const selectedZones =
    selectedZoneIds
      ?.map((zoneId) => zones.find((zone) => zoneId === zone.id))
      .filter((zone) => zone !== undefined) ?? zones;

  const { data: subjectsData, isLoading: isLoadingSubjects } =
    useListSubjectsSubjectsGetQuery({});

  const subjects = subjectsData?.data ?? [];

  if (isLoadingZones || isLoadingSubjects) {
    return (
      <Box
        sx={{
          display: "flex",
          flex: 1,
          flexDirection: "column",
          minHeight: "100vh",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flex: 1,
        flexDirection: "column",
        minHeight: "100vh",
      }}
    >
      <Stack
        direction={"row"}
        sx={{
          alignItems: "center",
          gap: 1.5,
          paddingInline: { xs: 1.5, md: 2.5, lg: 3 },
          paddingBlock: 1,
        }}
      >
        <NightReportFilter />
        <Box sx={{ flexGrow: 1 }} />
        <DynamicButton
          startIcon={<Printer />}
          sx={{ display: { xs: "none", md: "inline-flex" } }}
          color={"info"}
          onClick={() => globalThis.print()}
        >
          {t("print_report")}
        </DynamicButton>
        <ZoneSelector zones={zones} />
      </Stack>
      <Box
        sx={{ paddingInline: { xs: 1.5, md: 2.5, lg: 3 }, flexGrow: 1 }}
        id={"print-area"}
      >
        <NightReportLanding
          selectedZones={selectedZones}
          zones={zones}
          subjects={subjects}
        />
      </Box>
      <AppVersion />
    </Box>
  );
}
